export type JsonKind = 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null';

export interface TreeRow {
  id: string;
  depth: number;
  label: string;
  kind: JsonKind;
  preview: string;
  expandable: boolean;
  expanded: boolean;
  childCount: number;
  value: unknown;
}

export const ROOT_ID = '$';
const PREVIEW_LIMIT = 160;
const CHILD_CAP = 5000;

export function kindOf(value: unknown): JsonKind {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return 'array';
  const type = typeof value;
  if (type === 'object') return 'object';
  if (type === 'string' || type === 'number' || type === 'boolean') return type;
  return 'string';
}

export function childrenOf(value: unknown): [string, unknown][] {
  if (Array.isArray(value)) return value.slice(0, CHILD_CAP).map((item, index) => [String(index), item]);
  if (value !== null && typeof value === 'object') return Object.entries(value as Record<string, unknown>).slice(0, CHILD_CAP);
  return [];
}

export function sizeOf(value: unknown): number {
  if (Array.isArray(value)) return value.length;
  if (value !== null && typeof value === 'object') return Object.keys(value as Record<string, unknown>).length;
  return 0;
}

export function previewOf(value: unknown, kind: JsonKind): string {
  if (kind === 'object') return `{${sizeOf(value)}}`;
  if (kind === 'array') return `[${sizeOf(value)}]`;
  if (kind === 'null') return 'null';
  const text = kind === 'string' ? `"${String(value)}"` : String(value);
  return text.length > PREVIEW_LIMIT ? `${text.slice(0, PREVIEW_LIMIT)}…` : text;
}

const TILDE = new RegExp('~', 'g');
const SLASH = new RegExp('/', 'g');

export function childId(parent: string, key: string): string {
  return `${parent}/${key.replace(TILDE, '~0').replace(SLASH, '~1')}`;
}

interface Pending {
  id: string;
  label: string;
  value: unknown;
  depth: number;
}

export function visibleRows(root: unknown, expanded: ReadonlySet<string>, rootLabel: string): TreeRow[] {
  const rows: TreeRow[] = [];
  const stack: Pending[] = [{ id: ROOT_ID, label: rootLabel, value: root, depth: 0 }];
  while (stack.length > 0) {
    const node = stack.pop() as Pending;
    const kind = kindOf(node.value);
    const count = sizeOf(node.value);
    const expandable = count > 0;
    const open = expandable && expanded.has(node.id);
    rows.push({ id: node.id, depth: node.depth, label: node.label, kind, preview: previewOf(node.value, kind), expandable, expanded: open, childCount: count, value: node.value });
    if (open) {
      const kids = childrenOf(node.value);
      for (let index = kids.length - 1; index >= 0; index -= 1) {
        const [key, value] = kids[index] as [string, unknown];
        stack.push({ id: childId(node.id, key), label: key, value, depth: node.depth + 1 });
      }
    }
  }
  return rows;
}

export function expandAllIds(root: unknown, cap = 20_000): Set<string> {
  const ids = new Set<string>();
  const stack: { id: string; value: unknown }[] = [{ id: ROOT_ID, value: root }];
  while (stack.length > 0 && ids.size < cap) {
    const node = stack.pop() as { id: string; value: unknown };
    if (sizeOf(node.value) === 0) continue;
    ids.add(node.id);
    for (const [key, value] of childrenOf(node.value)) stack.push({ id: childId(node.id, key), value });
  }
  return ids;
}

export function defaultExpanded(root: unknown): Set<string> {
  const ids = new Set<string>();
  if (sizeOf(root) > 0) ids.add(ROOT_ID);
  return ids;
}

export interface SearchResult {
  matches: string[];
  reveal: Set<string>;
  truncated: boolean;
}

interface Searching {
  id: string;
  label: string;
  value: unknown;
  ancestors: string[];
}

export function searchTree(root: unknown, query: string, limit = 500): SearchResult {
  const needle = query.trim().toLowerCase();
  const result: SearchResult = { matches: [], reveal: new Set(), truncated: false };
  if (!needle) return result;
  const found: string[] = [];
  const stack: Searching[] = [{ id: ROOT_ID, label: '', value: root, ancestors: [] }];
  while (stack.length > 0) {
    const node = stack.pop() as Searching;
    const kind = kindOf(node.value);
    const leaf = kind !== 'object' && kind !== 'array';
    const hit = node.label.toLowerCase().includes(needle) || (leaf && String(node.value).toLowerCase().includes(needle));
    if (hit) {
      if (found.length >= limit) {
        result.truncated = true;
        break;
      }
      found.push(node.id);
      for (const ancestor of node.ancestors) result.reveal.add(ancestor);
    }
    if (!leaf) {
      const next = [...node.ancestors, node.id];
      for (const [key, value] of childrenOf(node.value)) stack.push({ id: childId(node.id, key), label: key, value, ancestors: next });
    }
  }
  result.matches = found;
  return result;
}

export function matchOrder(rows: readonly TreeRow[], matches: ReadonlySet<string>): string[] {
  return rows.filter((row) => matches.has(row.id)).map((row) => row.id);
}

export function indexOfRow(rows: readonly TreeRow[], id: string): number {
  return rows.findIndex((row) => row.id === id);
}

export function toJsonText(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? 'null';
  } catch {
    return String(value);
  }
}

export function windowOf(total: number, scrollTop: number, viewport: number, rowHeight: number, overscan = 8): { start: number; end: number } {
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const end = Math.min(total, Math.ceil((scrollTop + viewport) / rowHeight) + overscan);
  return { start, end };
}
